import React from "react";
import PropTypes from "prop-types";
import { Button, Col, Row } from "reactstrap";
import { Slot, SlotContent, SlotScope } from "shared/react_components/slots";
import IconButton from "./IconButton";

function wrapPreventDefault(e) {
  e.preventDefault();
  return e;
}

class ItemLayout extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired
  };

  render() {
    const { children, ...props } = this.props;

    return (
      <SlotScope content={children}>
        <Row {...props}>
          <Slot name={"item"} as={Col} xs={11} />

          <Slot name={"close-button"} as={Col} xs={1} />
        </Row>
      </SlotScope>
    );
  }
}

class Item extends React.Component {
  static propTypes = {
    children: PropTypes.object,
    hideClose: PropTypes.bool,
    disableClose: PropTypes.bool,
    onDelete: PropTypes.func.isRequired,
    disabledCloseButtonTooltip: PropTypes.string
  };

  static defaultProps = {
    hideClose: false,
    disableClose: false
  };

  render() {
    const { children, onDelete, hideClose, disableClose, disabledCloseButtonTooltip } = this.props;

    return (
      <ItemLayout className={"add-list__item"}>
        <SlotContent name={"item"}>{children}</SlotContent>

        {!hideClose && (
          <SlotContent name={"close-button"} className="col-form-label position-relative">
            <IconButton
              icon="close"
              tooltipText={disableClose ? disabledCloseButtonTooltip : ""}
              small={true}
              disabled={disableClose}
              onClick={e => onDelete(wrapPreventDefault(e))}
              data-action="delete-item"
              className="add-list__close p-0"
            />
          </SlotContent>
        )}
      </ItemLayout>
    );
  }
}

class View extends React.Component {
  static propTypes = {
    items: PropTypes.array.isRequired,
    children: PropTypes.func.isRequired,

    onItemDeleted: PropTypes.func.isRequired,
    onItemAdded: PropTypes.func.isRequired,
    addButtonVisible: PropTypes.bool,
    addButtonDisabled: PropTypes.bool,
    deleteButtonVisible: PropTypes.bool,
    canDeleteItem: PropTypes.func,
    disabledCloseButtonTooltip: PropTypes.string,
    customAddNewMessage: PropTypes.string,
    minItemsCount: PropTypes.number
  };

  static defaultProps = {
    addButtonVisible: true,
    addButtonDisabled: false,
    deleteButtonVisible: true,
    canDeleteItem: item => true,
    minItemsCount: 1
  };

  createItemDeletedHandler(index) {
    return () => {
      this.props.onItemDeleted(index);
    };
  }

  render() {
    const {
      items,
      children: renderProp,
      onItemAdded,
      canDeleteItem,
      deleteButtonVisible,
      disabledCloseButtonTooltip,
      customAddNewMessage,
      minItemsCount
    } = this.props;

    return (
      <div className="add-list">
        {items.map((item, index) => (
          <Item
            key={index}
            onDelete={this.createItemDeletedHandler(index)}
            disableClose={!canDeleteItem(item)}
            hideClose={!deleteButtonVisible || items.length <= minItemsCount}
            disabledCloseButtonTooltip={disabledCloseButtonTooltip}
          >
            {renderProp(item, index)}
          </Item>
        ))}

        {this.props.addButtonVisible && (
          <Button
            color="link"
            className="add-list__add-another px-0 py-1"
            disabled={this.props.addButtonDisabled}
            data-action="add-item"
            onClick={e => onItemAdded(wrapPreventDefault(e))}
          >
            <i className="material-icons">add</i>
            {customAddNewMessage || "Add New"}
          </Button>
        )}
      </div>
    );
  }
}

export default class Container extends React.Component {
  static propTypes = {
    items: PropTypes.array.isRequired,

    /**
     * An object holding the data for an empty row
     */
    emptyItem: PropTypes.object.isRequired,

    /**
     * A renderProp called to render a single row for an item.
     */
    children: PropTypes.func.isRequired,

    /**
     * Fires when removing or adding or modifying an item.
     */
    onChange: PropTypes.func.isRequired,

    /**
     * Whether "Add more" button is shown
     */
    addButtonVisible: PropTypes.bool,

    /**
     * Whether "Add more" button is disabled
     */
    addButtonDisabled: PropTypes.bool,

    /**
     * Whether "Delete item" button is shown
     */
    deleteButtonVisible: PropTypes.bool,

    /**
     * Function that takes in an item and is used to decide to disable the closeItem button.
     */
    canDeleteItem: PropTypes.func,

    /**
     * Disabled Close Button Tooltip
     */
    disabledCloseButtonTooltip: PropTypes.string,

    /**
     * Custom Add New item message
     */
    customAddNewMessage: PropTypes.string,
    /**
     * The minimum number of items. Once there are more items than this minimum, then the Delete Item button will appear
     */
    minItemsCount: PropTypes.number
  };

  static defaultProps = {
    addButtonVisible: true,
    addButtonDisabled: false,
    deleteButtonVisible: true,
    minItemsCount: 1
  };

  static Item = Item;
  static View = View;
  static ItemLayout = ItemLayout;

  handleFieldUpdated(index, updatedValues) {
    const before = this.props.items.slice(0, index);
    const after = this.props.items.slice(index + 1);

    const updatedItem = {
      ...this.props.items[index],
      ...updatedValues
    };

    this.props.onChange(before.concat([updatedItem]).concat(after));
  }

  handleItemDeleted = index => {
    const beforeItem = this.props.items.slice(0, index);
    const afterItem = this.props.items.slice(index + 1);

    this.props.onChange(beforeItem.concat(afterItem));
  };

  handleItemAdded = () => {
    this.props.onChange(this.props.items.concat([this.props.emptyItem]));
  };

  callRenderProp(item, index) {
    const fieldUpdater = field => {
      return value => {
        this.handleFieldUpdated(index, { [field]: value });
      };
    };

    fieldUpdater.fromEventTarget = field => {
      return e => fieldUpdater(field)(e.target.value);
    };

    fieldUpdater.updateItemValues = values => this.handleFieldUpdated(index, values);

    return this.props.children({ item, fieldUpdater, index });
  }

  render() {
    return (
      <View
        items={this.props.items}
        onItemDeleted={this.handleItemDeleted}
        onItemAdded={this.handleItemAdded}
        addButtonVisible={this.props.addButtonVisible}
        addButtonDisabled={this.props.addButtonDisabled}
        deleteButtonVisible={this.props.deleteButtonVisible}
        canDeleteItem={this.props.canDeleteItem}
        disabledCloseButtonTooltip={this.props.disabledCloseButtonTooltip}
        customAddNewMessage={this.props.customAddNewMessage}
        minItemsCount={this.props.minItemsCount}
      >
        {(item, index) => this.callRenderProp(item, index)}
      </View>
    );
  }
}
